import * as Sentry from "@sentry/react";

import protectedAxios from "../axios";
import { API_URL } from "../constants";

export const getUserConversationFromId = async (
  conversationId,
  successCallBack = () => {},
  failureCallBack = () => {}
) => {
  try {
    const { data } = await protectedAxios.get(
      `${API_URL}/v1/chat/conversation.getOne/${conversationId}`
    );

    successCallBack(data);
    return data;
  } catch (error) {
    // Don't report 404 to Sentry - conversation may be deleted or user unauthorized
    if (error?.status !== 404) {
      Sentry.captureException(error);
    }
    failureCallBack(error);
    throw error;
  }
};

export const createGroupConversation = async (payload) => {
  try {
    const { data } = await protectedAxios.post(
      `${API_URL}/v1/chat/conversation.create.group`,
      payload
    );

    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const createSpaceConversation = async (payload) => {
  try {
    const { data } = await protectedAxios.post(
      `${API_URL}/v1/chat/conversation.space.create`,
      payload
    );

    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getConversationParticipantIds = async (conversationId) => {
  try {
    const { data } = await protectedAxios.get(
      `${API_URL}/v1/chat/conversation.participantsIds/${conversationId}`
    );
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const updateConversation = async (payload) => {
  try {
    const { data } = await protectedAxios.patch(
      `${API_URL}/v1/chat/conversation.participant`,
      payload
    );
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const updateGroupInfo = async ({ id, ...payload }) => {
  try {
    const { data } = await protectedAxios.patch(
      `${API_URL}/v1/chat/conversation.group/${id}`,
      payload
    );
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const removeFromConversation = async (conversationId) => {
  try {
    const { data } = await protectedAxios.delete(
      `${API_URL}/v1/chat/conversation.participant/${conversationId}`
    );
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getSpaceByGamePlayerId = async (gamePlayerId) => {
  try {
    const { data } = await protectedAxios.get(
      `${API_URL}/v1/chat/conversation.space.get/gameplayer/${gamePlayerId}`
    );
    return data;
  } catch (error) {
    if (error?.status !== 404) {
      Sentry.captureException(error);
    }
    throw error;
  }
};

export const kickUsers = async ({ conversationId, participantIds }) => {
  try {
    const { data } = await protectedAxios.post(
      `${API_URL}/v1/chat/conversation.participants/kick/${conversationId}`,
      {
        participantIds,
      }
    );
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

/**
 * Staff-only (ADMIN / MODERATOR) list of every OTHER conversation a person is
 * in — DMs, groups, Spaces — newest activity first. Backs the Space sidebar's
 * "Other Conversations" section.
 *
 * @param {string} gamePlayerId Whose rooms to list.
 * @param {{ excludeConversationId?: string, page?: number, limit?: number }} [options]
 * @returns {Promise<{ conversations: Array, totalCount: number, page: number, limit: number }>}
 */
export const listUserConversationsForPeek = async (
  gamePlayerId,
  { excludeConversationId, page = 0, limit = 5 } = {}
) => {
  try {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (excludeConversationId) {
      params.set("excludeConversationId", excludeConversationId);
    }
    const { data } = await protectedAxios.get(
      `${API_URL}/v1/chat/peek/user/${gamePlayerId}/conversations?${params.toString()}`
    );
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

/**
 * Staff-only read-only transcript of a room the caller need not be in. Same
 * shape as the investor wall's transcript, so `ConversationModalLight`
 * renders it directly.
 *
 * @param {string} conversationId
 * @param {number} [limit=100] Newest N messages.
 */
export const getConversationMessagesForPeek = async (
  conversationId,
  limit = 100
) => {
  try {
    const { data } = await protectedAxios.get(
      `${API_URL}/v1/chat/peek/conversation/${conversationId}/messages?limit=${limit}`
    );
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

// --- Message pinning (docs/chat/message-pinning.md) -------------------------

/**
 * The conversation's pinned messages (newest pin first, max 5) plus whether
 * the caller may pin here: `{ conversationId, canPin, pinned: MessageDoc[] }`.
 */
export const getPinnedMessages = async (conversationId) => {
  const { data } = await protectedAxios.get(
    `${API_URL}/v1/chat/conversation.pinned/${conversationId}`
  );
  return data;
};

/** Pin one message. Resolves to `{ conversationId, messageId, pinned: { at, by } }`. */
export const pinMessage = async (conversationId, messageId) => {
  try {
    const { data } = await protectedAxios.post(
      `${API_URL}/v1/chat/conversation.message.pin`,
      { conversationId, messageId }
    );
    return data;
  } catch (error) {
    // 400 (limit) / 403 (not allowed) are told to the user by the caller.
    if (![400, 403].includes(error?.response?.status)) {
      Sentry.captureException(error);
    }
    throw error;
  }
};

/** Unpin one message. Resolves to `{ conversationId, messageId, pinned: null }`. */
export const unpinMessage = async (conversationId, messageId) => {
  try {
    const { data } = await protectedAxios.delete(
      `${API_URL}/v1/chat/conversation.message.pin`,
      { data: { conversationId, messageId } }
    );
    return data;
  } catch (error) {
    if (error?.response?.status !== 403) Sentry.captureException(error);
    throw error;
  }
};
